import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useIpcStateMutation, useIpcStateQuery } from '@highlight/ipc/react'

export interface ProfilePayload {
  dataPrivacyModeEnabled?: boolean
}

export interface ProfileData {
  id?: string
  email?: string
  firstName?: string
  lastName?: string
  dataPrivacyModeEnabled?: boolean
  /** True when an organization the user belongs to locks telemetry off; the toggle is then managed. */
  telemetryLockedByOrganization?: boolean
}

const PROFILE_QUERY_KEY = 'profile'

export function useProfile() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  const { data: profileData, isLoading } = useIpcStateQuery({
    queryKey: [PROFILE_QUERY_KEY],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const url = getChatApiRoute('api/v1/profiles/me')
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })
      if (!response.ok) {
        throw new Error('Failed to fetch profile')
      }
      const responseData = await response.json()
      // Handle both wrapped and unwrapped responses
      return (responseData.data ?? responseData) as ProfileData
    },
  })

  const updateProfile = useIpcStateMutation({
    queryKey: [PROFILE_QUERY_KEY],
    mutationFn: async (payload: ProfilePayload) => {
      const url = getChatApiRoute('api/v1/profiles/me')
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAuthToken()}`,
        },
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw new Error('Failed to update profile')
      }
      const responseData = await response.json()
      return (responseData.data ?? responseData) as ProfileData
    },
    onMutate: async (payload) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: [PROFILE_QUERY_KEY] })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<ProfileData>([PROFILE_QUERY_KEY])

      // Optimistically update to the new value
      if (previousData) {
        queryClient.setQueryData<ProfileData>([PROFILE_QUERY_KEY], {
          ...previousData,
          ...payload,
        })
      }

      // Return context with the previous value for rollback
      return { previousData }
    },
    onError: (_err, _payload, context) => {
      // Rollback to the previous value on error
      if (context?.previousData) {
        queryClient.setQueryData([PROFILE_QUERY_KEY], context.previousData)
      }
    },
    onSuccess: (data) => {
      // Update with the actual response from server
      queryClient.setQueryData([PROFILE_QUERY_KEY], data)
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're in sync
      queryClient.invalidateQueries({ queryKey: [PROFILE_QUERY_KEY] })
    },
  })

  return {
    profileData,
    isLoading,
    updateProfile: updateProfile.mutateAsync,
  }
}
